import React, { useState } from 'react';
import { isEmpty } from 'utils/types';
import { Trans, useTranslation } from 'react-i18next';
import { selectors as settingsSelectors } from 'state/settings';
import { selectors as authSelectors } from 'state/auth';
import {
  actions as downloadActions,
  selectors as downloadsSelectors,
} from 'state/downloads';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import useNavigateToMainPage from 'utils/hooks/useNavigateToMainPage';
// styles
import styled from 'styled-components';
// components
import { Radios, Button } from '@ontruck/orc';
import Select, { components } from 'components/CustomSelect';
import LoadSpinner from 'components/LoadSpinner';
import Link from 'components/styled/link';
import { Title, Text } from 'refactored/ui/typography';
import { Main, Separator, GridRow } from 'refactored/ui/layout';
import { ButtonMaxWithdOnMobile, Legend } from 'refactored/ui/form';
import AuthenticatedUserView from 'refactored/ui/views/authenticatedUserView';
import { userIsAuthenticatedRedir } from 'components/auth/hoc';
import { IS_TENANT } from 'tenant/utils';

const SingleValue = (props) => {
  const { selectProps } = props;
  const { value } = selectProps;
  return (
    <components.SingleValue {...props}>
      {value.abbr_label}
    </components.SingleValue>
  );
};

const Option = ({ children, ...props }) => (
  <components.Option {...props}>{children}</components.Option>
);

const getMonthOptionsSince = (dateString) => {
  const date = moment(dateString).startOf('month');
  const now = moment();
  const dates = [];

  do {
    now.startOf('month');
    const optionDate = {
      value: now.format('YYYY-MM-DD'),
      label: now.format('MMMM - YYYY'),
      abbr_label: now.format('MMMM - YYYY'),
    };
    dates.push(optionDate);
    now.subtract(1, 'month');
  } while (now.isSameOrAfter(date));

  return dates;
};

const Downloads = () => {
  const { t } = useTranslation('Downloads');
  const dispatch = useDispatch();
  const user = useSelector((state) => authSelectors.getUser(state));
  const isSubmitting = useSelector((state) =>
    downloadsSelectors.isSubmitting(state)
  );
  const isRequestSuccessful = useSelector((state) =>
    downloadsSelectors.isRequestSuccessful(state)
  );
  const monthOptions = getMonthOptionsSince(user.shipper_created_at);
  const [fileType, setFileType] = useState('pod');
  const [dateFrom, setDateFrom] = useState(
    getMonthOptionsSince(user.shipper_created_at)[0]
  );

  const handleDownloadFile = () => {
    dispatch(
      downloadActions.downloadFile({ dateFrom: dateFrom.value, fileType })
    );
  };
  const handleDownloadMore = () => {
    dispatch(downloadActions.setDownloadRequestSuccessful({ success: null }));
  };
  const setDownloadType = (_, { value }) => {
    setFileType(value);
  };
  const setSelectedDate = (value) => {
    setDateFrom(value);
  };

  return (
    <>
      <GridRow>
        <Title>{t('title')}</Title>
        <Title level="h2" variant="subtitle">
          {t('subtitle')}
        </Title>
      </GridRow>

      <GridRow>
        <Legend>{t('type_of_download_title')}</Legend>
        <Radios
          size="L"
          name="type"
          value={fileType}
          items={[
            { value: 'pod', label: t('type_of_download_pod_label') },
            { value: 'csv', label: t('type_of_download_csv_label') },
          ]}
          onChange={setDownloadType}
        />
      </GridRow>
      <Separator />
      <GridRow>
        <Legend>{t('filter_downloads_title')}</Legend>
        <Select
          value={dateFrom}
          label={t('date')}
          placeholder={t('date')}
          options={monthOptions}
          components={{ SingleValue, Option }}
          isSearchable={false}
          onChange={setSelectedDate}
        />
      </GridRow>
      <Separator />
      <GridRow>
        {isRequestSuccessful && (
          <Text variant="feedback-msg">
            {t('request_successful', { email: user.email })}
          </Text>
        )}

        {isRequestSuccessful !== null && !isRequestSuccessful && (
          <Text variant="feedback-msg">{t('request_error')}</Text>
        )}

        {isRequestSuccessful === null && (
          <ButtonMaxWithdOnMobile
            type="button"
            size="L"
            text={
              isSubmitting ? (
                <LoadSpinner skin="inButton" />
              ) : (
                t('submit_button_label')
              )
            }
            onClick={handleDownloadFile}
          />
        )}

        {isRequestSuccessful !== null && (
          <Button
            type="button"
            ghost
            size="L"
            text={t('more_data_button_label')}
            onClick={handleDownloadMore}
          />
        )}
      </GridRow>
      <Separator />
    </>
  );
};

const FiscalInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 20%);
  grid-gap: 10px;
`;

const BillingData = () => {
  const { t } = useTranslation('Settings');
  const { finance_email, finance_phone, fiscal_info } = useSelector(
    settingsSelectors.getCountrySettings
  );
  const { billing_contact, payment_method } = useSelector(
    authSelectors.getShipper
  );

  return (
    <>
      <GridRow>
        <Title>{t('page_title')}</Title>
        <Title level="h2">{t('billing_contact.title')}</Title>

        <Text variant="label">{t('billing_contact.name')}</Text>
        <Text>
          {billing_contact && billing_contact.name}{' '}
          {billing_contact && billing_contact.last_name}
        </Text>

        <Text variant="label">{t('billing_contact.phone')}</Text>
        <Text>{billing_contact && billing_contact.phone}</Text>

        <Text variant="label">{t('billing_contact.email')}</Text>
        <Text>{billing_contact && billing_contact.email}</Text>
      </GridRow>

      <Separator />

      {!isEmpty(payment_method) && (
        <>
          <GridRow>
            <Title level="h2">{t('payment_method.title')}</Title>

            <Text variant="label" />
            <Text>{t(`payment_method.methods.${payment_method}`)}</Text>
          </GridRow>
        </>
      )}
      <Separator />

      <GridRow>
        <Title level="h2">{t('fiscal_info.title')}</Title>
        <FiscalInfoContainer>
          <Text variant="label">{t('fiscal_info.company_name')}</Text>
          <Text variant="label">{t('shipper_tax_number')}</Text>
          <Text>{fiscal_info && fiscal_info.company_name}</Text>
          <Text>{fiscal_info && fiscal_info.tax_number}</Text>
        </FiscalInfoContainer>
        <Text variant="label">{t('fiscal_info.address')}</Text>
        <Text>{fiscal_info && fiscal_info.address}</Text>
      </GridRow>
      <GridRow>
        <Text variant="label">
          <Trans
            t={t}
            i18nKey="payment_method.info"
            values={{
              billingEmail: finance_email,
              billingPhone: finance_phone,
            }}
            components={[
              <Link
                href={`mailto:${finance_email}`}
                text={finance_email}
                noTextDecoration
                target="_blank"
              />,
              <Link
                href={`tel:${finance_phone}`}
                text={finance_phone}
                noTextDecoration
                target="_blank"
              />,
            ]}
          />
        </Text>
      </GridRow>
    </>
  );
};

const BillingDataTenant = () => {
  const { t } = useTranslation('Settings');
  const { billing_contact, payment_method } = useSelector(
    authSelectors.getShipper
  );

  const finance_email = 'facturacion@intraser.es';
  const finance_phone = '964 200 400';

  return (
    <>
      <GridRow>
        <Title>{t('page_title')}</Title>
        <Title level="h2">{t('billing_contact.title')}</Title>

        <Text variant="label">{t('billing_contact.name')}</Text>
        <Text>
          {billing_contact && billing_contact.name}{' '}
          {billing_contact && billing_contact.last_name}
        </Text>

        <Text variant="label">{t('billing_contact.phone')}</Text>
        <Text>{billing_contact && billing_contact.phone}</Text>

        <Text variant="label">{t('billing_contact.email')}</Text>
        <Text>{billing_contact && billing_contact.email}</Text>
      </GridRow>

      <Separator />

      {!isEmpty(payment_method) && (
        <>
          <GridRow>
            <Title level="h2">{t('payment_method.title')}</Title>

            <Text variant="label" />
            <Text>{t(`payment_method.methods.${payment_method}`)}</Text>
          </GridRow>
        </>
      )}
      <Separator />

      <GridRow>
        <Title level="h2">{t('fiscal_info.title')}</Title>
        <FiscalInfoContainer>
          <Text variant="label">{t('fiscal_info.company_name')}</Text>
          <Text variant="label">{t('shipper_tax_number')}</Text>
          <Text>Transargi, S.L.U</Text>
          <Text>B43062611</Text>
        </FiscalInfoContainer>
        <Text variant="label">{t('fiscal_info.address')}</Text>
        <Text>
          Av. Ferrandis Salvador, 3, entl, 12100 Grao de Castellón, Castellón
        </Text>
      </GridRow>
      <GridRow>
        <Text variant="label">
          <Trans
            t={t}
            i18nKey="payment_method.info"
            values={{
              billingEmail: finance_email,
              billingPhone: finance_phone,
            }}
            components={[
              <Link
                href={`mailto:${finance_email}`}
                text={finance_email}
                noTextDecoration
                target="_blank"
              />,
              <Link
                href={`tel:${finance_phone}`}
                text={finance_phone}
                noTextDecoration
                target="_blank"
              />,
            ]}
          />
        </Text>
      </GridRow>
    </>
  );
};

const BillingPage = ({ className }) => {
  const handleBack = useNavigateToMainPage();

  return (
    <AuthenticatedUserView className={className} handleBack={handleBack}>
      <Main>
        <Downloads />
        {!IS_TENANT && <BillingData />}
        {IS_TENANT && <BillingDataTenant />}
      </Main>
    </AuthenticatedUserView>
  );
};

export default userIsAuthenticatedRedir(BillingPage);
